import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { mockOffers, mockProducts } from '../data/mockData';
import { BrandHero } from '../components/BrandHero';
import { CashbackDetailsCard } from '../components/CashbackDetailsCard';
import { ProductCard } from '../components/ProductCard';
import { CheckCircle, Search, Filter } from 'lucide-react';
export function OfferDetailPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const offer = mockOffers.find(o => o.id === id);
  const brandProducts = mockProducts.filter(p => p.brandId === id);
  if (!offer) {
    return <div className="max-w-6xl mx-auto px-4 py-12 text-center" data-id="element-408">
        <h1 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-409">
          Brand Not Found
        </h1>
        <Link to="/explore" className="text-kithward-700 text-xl hover:underline font-bold" data-id="element-410">
          Return to Explore
        </Link>
      </div>;
  }
  return <div className="min-h-screen bg-gray-50 pb-20" data-id="element-411">
      {/* 1. Brand Hero Section */}
      <BrandHero offer={offer} data-id="element-412" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 -mt-8 relative z-10" data-id="element-413">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-414">
          {/* Left Column: About & Cashback */}
          <div className="lg:col-span-2 space-y-8" data-id="element-415">
            {/* About Section */}
            <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100" data-id="element-416">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-417">
                About {offer.merchant}
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6" data-id="element-418">
                {offer.about || offer.longDescription}
              </p>

              {offer.benefits && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-419">
                  {offer.benefits.map((benefit, idx) => <div key={idx} className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg" data-id="element-420">
                      <CheckCircle className="w-5 h-5 text-kithward-700 flex-shrink-0" data-id="element-421" />
                      <span className="font-medium text-gray-800" data-id="element-422">
                        {benefit}
                      </span>
                    </div>)}
                </div>}
            </div>

            {/* Cashback Details */}
            <CashbackDetailsCard offer={offer} data-id="element-423" />
          </div>

          {/* Right Column: Quick Actions / Summary (Optional - kept simple for now) */}
          <div className="hidden lg:block" data-id="element-424">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 sticky top-8" data-id="element-425">
              <h3 className="font-bold text-gray-900 text-lg mb-4" data-id="element-426">
                Why Shop {offer.merchant}?
              </h3>
              <ul className="space-y-4" data-id="element-427">
                <li className="flex items-start gap-3" data-id="element-428">
                  <div className="bg-green-100 p-1.5 rounded-full mt-0.5" data-id="element-429">
                    <CheckCircle className="w-4 h-4 text-green-700" data-id="element-430" />
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-431">
                    Verified Senior-Friendly Products
                  </p>
                </li>
                <li className="flex items-start gap-3" data-id="element-432">
                  <div className="bg-green-100 p-1.5 rounded-full mt-0.5" data-id="element-433">
                    <CheckCircle className="w-4 h-4 text-green-700" data-id="element-434" />
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-435">
                    Exclusive {offer.cashbackPercent}% Cash Back Rate
                  </p>
                </li>
                <li className="flex items-start gap-3" data-id="element-436">
                  <div className="bg-green-100 p-1.5 rounded-full mt-0.5" data-id="element-437">
                    <CheckCircle className="w-4 h-4 text-green-700" data-id="element-438" />
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-439">
                    Trusted by 10,000+ Kithward Members
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Product Catalog Section */}
        <div className="mt-16" data-id="element-440">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8" data-id="element-441">
            <div data-id="element-442">
              <h2 className="text-3xl font-serif font-bold text-gray-900 mb-2" data-id="element-443">
                Featured Products
              </h2>
              <p className="text-gray-500 text-lg" data-id="element-444">
                Top picks from {offer.merchant}
              </p>
            </div>

            {/* Search & Filter Bar */}
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto" data-id="element-445">
              <div className="relative" data-id="element-446">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" data-id="element-447" />
                <input type="text" placeholder={`Search ${offer.merchant}...`} className="pl-10 pr-4 py-2.5 border border-gray-300 rounded-full w-full sm:w-64 focus:ring-2 focus:ring-kithward-700 focus:border-transparent outline-none" data-id="element-448" />
              </div>
              <button className="flex items-center justify-center gap-2 px-5 py-2.5 border border-gray-300 rounded-full hover:bg-gray-50 font-medium text-gray-700" data-id="element-449">
                <Filter className="w-4 h-4" data-id="element-450" />
                Filters
              </button>
            </div>
          </div>

          {/* Product Grid */}
          {brandProducts.length > 0 ? <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-id="element-451">
              {brandProducts.map(product => <ProductCard key={product.id} product={product} data-id="element-452" />)}
              {/* Add some placeholder products to fill grid if needed */}
              {brandProducts.length < 4 && <div className="bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center p-8 text-center h-full min-h-[300px]" data-id="element-453">
                  <p className="text-gray-500 font-medium mb-2" data-id="element-454">
                    More products coming soon
                  </p>
                  <button className="text-kithward-700 font-bold hover:underline" data-id="element-455">
                    View all on {offer.merchant} site
                  </button>
                </div>}
            </div> : <div className="bg-white rounded-xl p-12 text-center border border-gray-200" data-id="element-456">
              <p className="text-gray-500 text-lg mb-4" data-id="element-457">
                No products available to display right now.
              </p>
              <button className="bg-kithward-700 text-white px-8 py-3 rounded-full font-bold hover:bg-kithward-800 transition-colors" data-id="element-458">
                Visit {offer.merchant} Website
              </button>
            </div>}
        </div>
      </div>
    </div>;
}